import { TFunction, useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useMutation, UseMutationOptions } from '@mint-lib/query-client';

import { getErrorMessage } from '../utils/api.js';

export type EnhancedMutationExtraOptions = {
  meta?: { getErrorsMap?: (t: TFunction) => Record<string, string> };
};

export const useEnhancedMutation = <
  TData,
  TError,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext> &
    EnhancedMutationExtraOptions,
) => {
  const { t } = useTranslation('@myn/import-hub');

  return useMutation<TData, TError, TVariables, TContext>({
    ...options,
    onError(error, variables, context, ctx) {
      const self = this as typeof options;
      if (self.meta?.getErrorsMap) {
        showSnackbarForError(t, error, self.meta.getErrorsMap);
      }
      options.onError?.(error, variables, context, ctx);
    },
  });
};

const showSnackbarForError = (
  t: TFunction,
  error: unknown,
  getErrorsMap: (t: TFunction) => Record<string, string> = () => ({}),
) => {
  const errorsMap = getErrorsMap(t);

  const errorMessage = getErrorMessage(t, error, errorsMap);

  enqueueSnackbar({
    variant: 'error',
    title: t('Server error'),
    subTitle: errorMessage,
    textLayout: 'multiline',
    removable: true,
  });
};
